// @ts-strict-ignore
import { Injectable } from '@angular/core';
import {
  LMToolbarMoreButtonMenuParams,
  LMToolbarServiceBase
} from '@toolbar/lm-toolbar-service.base';
import { AppConstants } from '@utils/app.constants';
import { LMToolbarServiceAdapter } from '@toolbar/lm-toolbar-service-adapter';
import { LMContextService } from '@services/lm-context.service';
import { LMPageSpecUtilService } from './lm-page-spec-util.service';
import { LMConditionUtilService } from '@app/utils';

@Injectable()
export class LMPageToolbarService extends LMToolbarServiceBase {
  private isSmartPhone = false;
  private overflowAllActions = false;

  constructor(
    private lmContextService: LMContextService,
    private lmPageSpecUtilService: LMPageSpecUtilService,
    private lmConditionUtilService: LMConditionUtilService,
    public lmToolbarServiceAdapter: LMToolbarServiceAdapter,
  ) {
    super(lmToolbarServiceAdapter); // must msg the base class constructor
    this.isSmartPhone = this.lmContextService.isSmartPhone();
    this.overflowAllActions = this.lmContextService.hidePageLevelActions();
  }

  public buildToolbarButtons(pageSpec: LMPageSpec, pageStateInfo: PageStateInfo): void {
    this.updateToolbar(pageSpec, pageStateInfo);
  }

  public updateToolbar(pageSpec: LMPageSpec, pageStateInfo: PageStateInfo) {
    if (pageSpec.pageSpec.toolbar) {
      this.toolbarButtons = this.getCalledOutToolbarButtons(pageSpec);
    } else {
      const actionRefs: LMBusActionReference[] = this.getPageToolbarActionRefs(pageSpec);
      this.toolbarButtons = this.pageActionRefArrayToToolbarButtonArray(pageSpec, actionRefs);
    }
    this._updateToolbarState(this.toolbarButtons, pageStateInfo);
    this.fireToolbarObservable();
  }

  private getPageToolbarActionRefs(pageSpec: LMPageSpec): LMBusActionReference[] {
    const context = this.lmContextService.getContext();
    const noPdfInConfig = context.configuredFeatures.indexOf(AppConstants.FEATURE_ExportToPDF) !== -1;
    const noPagePdf = context.hidePagePdf;  // config parameter to revert pagePDF feature
    const actionReferenceArray = [];

    if (!noPagePdf && (!context.disallowExport && noPdfInConfig && this.displayPDFExportButton(pageSpec))) {
      actionReferenceArray.push(this.getStandardToolbarActions().printToPDF);
    }

    if (this.lmPageSpecUtilService.allowUpdate(pageSpec)) {
      actionReferenceArray.push(this.getStandardToolbarActions().save);
    }

    actionReferenceArray.push(this.getStandardToolbarActions().refresh);

    return actionReferenceArray;
  }

  private pageActionRefArrayToToolbarButtonArray(pageSpec: LMPageSpec, actionRefArray: LMBusActionReference[]): LMToolbarButton[] {
    return actionRefArray.map((actionRef) => {
      // don't overflow save action
      const overflowAction = actionRef.id === this.getStandardToolbarActions().save.id ? false : this.overflowAllActions;
      return this.buildLMToolbarButton(pageSpec, actionRef, true, false, this.isSmartPhone || overflowAction);
    });
  }

  private getCalledOutToolbarButtons(pageSpec: LMPageSpec): Array<LMToolbarButton> {
    // make a deep clone of the toolbar so the relActionUrl does not get over-written
    const calledOutActionRefs = pageSpec.pageSpec.toolbar;
    let calledOutToolbarButtons: Array<LMToolbarButton> = this.actionRefArrayToToolbarButtonArray(pageSpec, calledOutActionRefs, this.isSmartPhone);

    const actionRefs: LMBusActionReference[] = this.getPageToolbarActionRefs(pageSpec);
    const moreMenuItems = this.pageActionRefArrayToToolbarButtonArray(pageSpec, actionRefs);
    this.removeDuplicates(calledOutToolbarButtons, moreMenuItems);
    moreMenuItems.forEach((button: LMToolbarButton) => button.isMoreMenuItem = true);
    calledOutToolbarButtons = [ ...calledOutToolbarButtons, ...moreMenuItems ];

    return calledOutToolbarButtons;
  }

  // remove the duplication entries from moreMenuItems that exists in called out actions
  private removeDuplicates(calledOutActions: Array<LMToolbarButton>, moreMenuItems: Array<LMToolbarButton>): void {
    const unshiftIndex: number[] = [];
    moreMenuItems.forEach((moreButton: LMToolbarButton, index: number) => {
      if (calledOutActions.find((button: LMToolbarButton) => button.actionReference.id === moreButton.actionReference.id)) {
        // more menu items contains a button that has been called out, remove it from moreMenuItems
        unshiftIndex.push(index);
      }
    });
    // remove the higher index entries first, so the indexes don't get messed up
    unshiftIndex.slice().reverse().forEach((index: number) => moreMenuItems.splice(index, index + 1));
  }

  // If it is only MenuPanel do not show the PDF
  private displayPDFExportButton(pageSpec: LMPageSpec): boolean {

    if (pageSpec.pageSpec?.panels?.length === 1
        && (pageSpec.pageSpec?.panels[0]?.pane?.paneType === 'MenuPanel'
            || pageSpec.pageSpec?.panels[0]?.pane?.paneType === 'menuPanel')) {
      return false;
    }

    return true;
  }

  // Checks if panel is supported for PDF export
  private isPdfExportSupported(panel: LMPagePanelSpec): boolean {
    if (!panel) {
      return false;
    }

    if (panel.panelType === 'MultiPanePanel') {
      return true;
    }

    if (panel.panelType === 'Panel' && panel.pane.listSpec) {
      return !panel.pane.listSpec.busListSpec?.implementsOrgChartView;
    }

    if (panel.panelType === 'DashBoardPanel') {
      for (const pane of panel.panes) {
        if (!pane.listSpec) {
          return false;
        }

        if (pane.listSpec.busListSpec?.implementsOrgChartView) {
          return false;
        }
      }
      return true;
    }

    return false;
  }

  private _updateToolbarState(toolbarButtons: LMToolbarButton[], pageStateInfo: PageStateInfo) {
    toolbarButtons.forEach((toolbarButton: LMToolbarButton) => {
      switch(toolbarButton.actionReference.type) {
        case AppConstants.BUTTON_TYPE_RELATED:
          this.relatedActionState(toolbarButton, pageStateInfo.dataView);
          break;
        case AppConstants.BUTTON_TYPE_STANDARD_ACTION:
          this.standardActionState(toolbarButton, pageStateInfo);
          break;
        case AppConstants.BUTTON_TYPE_BUSTASK:
          this.busTaskActionState(toolbarButton, pageStateInfo);
          break;
      }
    });
  }

  public buildMoreButtonMenu(
    spec: LMFormSpec | LMListSpec | LMPageSpec,
    response: SohoPopupMenuResponseFunction,
    options: any,
    buttons: Array<LMToolbarButton>,
    moreButtonParams: LMToolbarMoreButtonMenuParams
  ): void {
    response(this.buildFirstLevelMoreMenu(spec, buttons, false));
  }

  private relatedActionState(
      toolbarButton: LMToolbarButton,
      dataView: LMDataView,
  ): LMToolbarButton {
    let valid = true;
    if (dataView && toolbarButton.actionReference.validity) {
      valid = this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, dataView);
    }

    // disabled if not valid
    toolbarButton.state.disabled = !valid;

    // hide if (invalid and hideWhenInvalid === true)
    toolbarButton.state.visible = !(!valid && toolbarButton.actionReference.hideWhenInvalid);

    toolbarButton.onPage = true;

    return toolbarButton;
  }

  private standardActionState(toolbarButton: LMToolbarButton, pageStateInfo: PageStateInfo) {
    let visible = true;
    let valid = false;
    const dataView = pageStateInfo.dataView;

    if (toolbarButton.actionReference.id === AppConstants.STANDARD_SAVE) {
      valid = pageStateInfo.isModified;
    } else if (toolbarButton.actionReference.id === AppConstants.STANDARD_TOOLBAR_EXPORTPDF) {
      // Disables PDF button for unsupported types
      valid = this.isPdfExportSupported(pageStateInfo.selectedPanel);
      visible = !this.lmContextService.getContext().hidePagePdf;
    } else if (toolbarButton.actionReference.id === AppConstants.STANDARD_TOOLBAR_REFRESH) {
      valid = true;
    }

    if (dataView && toolbarButton.actionReference.validity) {
      valid = this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, dataView);
    }

    toolbarButton.state.disabled = !valid;
    toolbarButton.state.visible = visible;
  }

  private busTaskActionState(toolbarButton: LMToolbarButton, pageStateInfo: PageStateInfo): void {
    const dataView: LMDataView = pageStateInfo.dataView;
    const valid = toolbarButton.actionReference.validity
        ? this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, dataView)
        : true;

    // disabled if not valid
    toolbarButton.state.disabled = !valid;

    // hide if (invalid and hideWhenInvalid === true)
    toolbarButton.state.visible = !(!valid && toolbarButton.actionReference.hideWhenInvalid);
  }
}
